export type BillingPlanOption = {
  id: number
  display_name?: string
  plan_name?: string
  label?: string
  price?: number | string | {
    value?: number | string
    currency_iso?: string
  }
  currency?: string
  symbol?: string
  showcase_price?: number | string | {
    value?: number | string
    currency_iso?: string
  }
}

export type ConnectedPlatformOption = {
  id?: number | string
  display_name?: string
  name?: string
  code?: string
  platform_info?: {
    display_name?: string
    name?: string
    code?: string
  }
}

export type FilterOption = {
  value: string
  label: string
}

export const statusFilterOptions: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
]

export const sourceFilterOptions: FilterOption[] = [
  { value: 'WIX', label: 'WIX' },
  { value: 'COMMERCIUM', label: 'COMMERCIUM' },
  { value: 'WOOCOMMERCE', label: 'WOOCOMMERCE' },
  { value: 'ODOO', label: 'ODOO' },
  { value: 'SHOPIFY', label: 'SHOPIFY' },
  { value: 'BIGCOMMERCE', label: 'BIGCOMMERCE' },
  { value: 'ECWID', label: 'ECWID' },
  { value: 'LOYVERSE', label: 'LOYVERSE' },
  { value: 'ICONIC', label: 'ICONIC' },
  { value: 'VINTED', label: 'VINTED' },
  { value: 'THERANGEPLUS', label: 'RANGE' },
  { value: 'VEEQO', label: 'VEEQO' },
  { value: 'QBO', label: 'QBO' },
  { value: 'GOOGLE', label: 'GOOGLE' },
  { value: 'DISCONNECT', label: 'DISCONNECT' },
]

export const filterSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    borderColor: '#DBDFE9',
    boxShadow: 'none',
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: '36px',
    padding: '0 0.75rem',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: '36px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#252F4A',
    whiteSpace: 'nowrap',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 1055,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1055,
  }),
}

export const multiFilterSelectStyles = {
  ...filterSelectStyles,
  valueContainer: (base) => ({
    ...base,
    minHeight: '36px',
    padding: '0 0.75rem',
    flexWrap: 'wrap',
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: '140px',
    flexShrink: 0,
  }),
}

const getCurrencySymbol = (currency?: string) => {
  if (!currency || currency === 'USD' || currency === '$') {
    return '$'
  }

  return `${currency} `
}

const getPriceValue = (price?: BillingPlanOption['price'] | BillingPlanOption['showcase_price']) => {
  if (price === undefined || price === null) {
    return undefined
  }

  return typeof price === 'object' ? price.value : price
}

const getPriceCurrency = (price?: BillingPlanOption['price'] | BillingPlanOption['showcase_price']) => {
  return price && typeof price === 'object' ? price.currency_iso : undefined
}

const formatPlanAmount = (amount?: number | string, currency?: string) => {
  if (amount === undefined || amount === null || amount === '') {
    return ''
  }

  const amountNumber = Number(amount)
  const formattedAmount = Number.isNaN(amountNumber)
    ? String(amount)
    : amountNumber.toLocaleString(undefined, {
      minimumFractionDigits: Number.isInteger(amountNumber) ? 0 : 2,
      maximumFractionDigits: 2,
    })

  return `${getCurrencySymbol(currency)}${formattedAmount}`
}

export const getBillingPlanLabel = (plan: BillingPlanOption) => {
  if (plan.label) {
    return plan.label
  }

  const planName = plan.display_name || plan.plan_name || `Plan ${plan.id}`
  const amount = formatPlanAmount(
    getPriceValue(plan.price) ?? getPriceValue(plan.showcase_price),
    getPriceCurrency(plan.price) ?? getPriceCurrency(plan.showcase_price) ?? plan.currency ?? plan.symbol
  )

  return amount ? `${planName} (${amount})` : planName
}

export const getResponseList = (data: unknown) => {
  if (Array.isArray(data)) {
    return data
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>

    if (Array.isArray(record.data)) {
      return record.data
    }

    if (Array.isArray(record.results)) {
      return record.results
    }

    if (Array.isArray(record.connected_platforms)) {
      return record.connected_platforms
    }

    if (Array.isArray(record.platforms)) {
      return record.platforms
    }
  }

  return []
}

export const getConnectedPlatformLabel = (platform: ConnectedPlatformOption) => {
  return (
    platform.display_name ||
    platform.platform_info?.display_name ||
    platform.name ||
    platform.platform_info?.name ||
    platform.code ||
    platform.platform_info?.code ||
    ''
  )
}

export const areSameArrays = (first: string[], second: string[]) => {
  return first.length === second.length && first.every((value, index) => value === second[index])
}

export const buildBillingPlanFilterOptions = (billingPlans: BillingPlanOption[]): FilterOption[] => {
  const labelCounts = billingPlans.reduce<Map<string, number>>((counts, plan) => {
    const label = getBillingPlanLabel(plan)
    counts.set(label, (counts.get(label) || 0) + 1)
    return counts
  }, new Map())

  return billingPlans
    .map((plan) => {
      const baseLabel = getBillingPlanLabel(plan)
      const label = (labelCounts.get(baseLabel) || 0) > 1 ? `${baseLabel} #${plan.id}` : baseLabel

      return {
        value: String(plan.id),
        label,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const buildPlatformFilterOptions = (connectedPlatforms: ConnectedPlatformOption[]): FilterOption[] => {
  return connectedPlatforms
    .map((platform) => {
      const label = getConnectedPlatformLabel(platform)
      const value = platform.id ?? platform.code ?? platform.name ?? label

      return label && value !== undefined && value !== null
        ? { value: String(value), label }
        : null
    })
    .filter((option): option is FilterOption => option !== null)
}
